import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import Activity from "components/DigiHapi/Activity.js";
import {
	ForgotPasswordButton,
	SubmitButton,
	loginCardSx
} from "components/DigiHapi/loginStyles";
import CustomFilledInput from "components/DigiHapi/CustomFilledInput";
import HomeHeader from "components/DigiHapi/HomeHeader";
import HomeFooter from "components/DigiHapi/HomeFooter";
import FullPageImage from "components/DigiHapi/FullPageImage";
import SnackBarContainer from "components/DigiHapi/SnackBarContainer";

import { fetchResetPassword } from "../../actions/login";
import { t } from "../../services/i18n";
import { parse } from "search-params";

// Redux
import { useDispatch, useSelector } from "react-redux";

const MIN_PASSWORD_LENGTH = 8;

export default function ResetPasswordPage(props) {
	const { location, history } = props;

	const dispatch = useDispatch();

	const { token } = parse(location.search || "");

	const [password, setPassword] = useState("");
	const [confirmation, setConfirmation] = useState("");
	const [passwordError, setPasswordError] = useState(null);
	const [confirmationError, setConfirmationError] = useState(null);

	const { isFetching, snackInfoMsg, snackErrorMsg, resetPasswordSuccess } =
		useSelector(state => state.loginReducer);

	useEffect(() => {
		if (resetPasswordSuccess) {
			// Password set: the success message is kept in the snackbar and shown on
			// the login page after the redirect.
			history.push("/connexion");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resetPasswordSuccess]);

	const validate = () => {
		let passwordErr = null;
		let confirmationErr = null;

		if (!password) {
			passwordErr = t("login:resetmissingpassword");
		} else if (password.length < MIN_PASSWORD_LENGTH) {
			passwordErr = t("login:resetbadformat");
		}

		if (!confirmation) {
			confirmationErr = t("login:resetmissingconfirm");
		} else if (confirmation !== password) {
			confirmationErr = t("login:resetbadconfirm");
		}

		setPasswordError(passwordErr);
		setConfirmationError(confirmationErr);

		return !passwordErr && !confirmationErr;
	};

	const onSubmit = event => {
		event.preventDefault();
		if (validate()) {
			dispatch(fetchResetPassword(token, password));
		}
	};

	return (
		<FullPageImage
			bgImage="home"
			child={
				<Grid container justifyContent="center">
					<HomeHeader bgDarkGray />
					<Grid item xs={12} sm={12} md={6} lg={4} px={2}>
						<form onSubmit={onSubmit}>
							<Card sx={loginCardSx}>
								<SnackBarContainer
									place="bc"
									snackInfoMsg={snackInfoMsg}
									snackErrorMsg={snackErrorMsg}
								>
									<MDBox display="flex" flexDirection="column" px={2} pt={2}>
										<MDTypography
											variant="h5"
											textAlign="center"
											sx={{ color: "#444041", mb: 2 }}
										>
											{t("login:resettitle")}
										</MDTypography>
										{token ? (
											<>
												<CustomFilledInput
													labelText={t("login:resetnewpassword")}
													id="newpassword"
													error={passwordError !== null}
													helperText={passwordError ? passwordError : undefined}
													formControlProps={{
														fullWidth: true
													}}
													inputProps={{
														value: password,
														endAdornment: (
															<InputAdornment position="end">
																<Icon>lock_outline</Icon>
															</InputAdornment>
														),
														type: "password",
														autoComplete: "off",
														disabled: isFetching,
														onChange: e => {
															setPassword(e.target.value);
															setPasswordError(null);
														}
													}}
												/>
												<CustomFilledInput
													labelText={t("login:resetconfirm")}
													id="confirmpassword"
													error={confirmationError !== null}
													helperText={
														confirmationError ? confirmationError : undefined
													}
													formControlProps={{
														fullWidth: true
													}}
													inputProps={{
														value: confirmation,
														endAdornment: (
															<InputAdornment position="end">
																<Icon>lock_outline</Icon>
															</InputAdornment>
														),
														type: "password",
														autoComplete: "off",
														disabled: isFetching,
														onChange: e => {
															setConfirmation(e.target.value);
															setConfirmationError(null);
														}
													}}
												/>
											</>
										) : (
											<MDTypography
												variant="body2"
												textAlign="center"
												sx={{ color: "#444041", mb: 2 }}
											>
												{t("login:resetinvalidlink")}
											</MDTypography>
										)}
									</MDBox>
								</SnackBarContainer>
								<MDBox
									display="flex"
									flexDirection="column"
									alignItems="center"
									px={2}
									pb={2}
								>
									<ForgotPasswordButton
										onClick={() => history.push("/connexion")}
									>
										{t("login:resetbacktologin")}
									</ForgotPasswordButton>
									{token && (
										<SubmitButton disabled={isFetching}>
											{isFetching ? <Activity /> : t("login:resetsubmit")}
										</SubmitButton>
									)}
								</MDBox>
							</Card>
						</form>
					</Grid>
					<HomeFooter bgDarkGray />
				</Grid>
			}
		/>
	);
}
